import { supabase } from './supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const NYLAS_CLIENT_ID = import.meta.env.VITE_NYLAS_CLIENT_ID;

export interface NylasAuthResult {
  success: boolean;
  error?: string;
  grantId?: string;
  email?: string;
  redirectPath?: string;
}

export async function initiateNylasAuth(redirectPath?: string): Promise<void> {
  const callbackUri = `${window.location.origin}/outlook-callback`;

  // Store redirect path for after callback
  sessionStorage.setItem('nylas_redirect_path', redirectPath || '/email');
  sessionStorage.setItem('nylas_auth_pending', 'true');

  // Build Nylas hosted auth URL directly (no edge function needed for this)
  const authUrl = new URL('https://api.us.nylas.com/v3/connect/auth');
  authUrl.searchParams.set('client_id', NYLAS_CLIENT_ID);
  authUrl.searchParams.set('redirect_uri', callbackUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('provider', 'microsoft');
  authUrl.searchParams.set('access_type', 'offline');

  window.location.href = authUrl.toString();
}

export async function handleNylasCallback(): Promise<NylasAuthResult> {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      return { success: false, error: urlParams.get('error_description') || error };
    }

    if (!code) {
      return { success: false, error: 'Missing authorization code' };
    }

    // Get current user session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return { success: false, error: 'Not authenticated. Please sign in first.' };
    }

    const callbackUri = `${window.location.origin}/outlook-callback`;

    // Exchange code via our edge function
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/nylas-auth?action=callback`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, callbackUri }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || 'Failed to connect' };
    }

    const result = await response.json();

    // Get stored redirect path
    const redirectPath = sessionStorage.getItem('nylas_redirect_path') || '/email';
    sessionStorage.removeItem('nylas_redirect_path');
    sessionStorage.removeItem('nylas_auth_pending');

    return {
      success: true,
      grantId: result.grantId,
      email: result.email,
      redirectPath,
    };
  } catch (err: any) {
    return { success: false, error: err.message || 'Unexpected error' };
  }
}

export async function disconnectNylas(grantId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/nylas-auth?action=disconnect`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ grantId }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to disconnect');
  }
}
